import { runSimulation, type HumanoidModel, type SimState, type Simulation } from '@/domain/hs1'

/**
 * Ein kleiner Zwischenspeicher vor dem Rechenkern.
 *
 * WARUM ES IHN GIBT. Ein Durchlauf — Gangerzeugung, Bodenkontakt, inverse
 * Dynamik, Antriebsliste — kostet in der Grössenordnung von zehn bis hundert
 * Millisekunden. Das ist für eine Seite, die auf einen Regler reagiert, in
 * Ordnung. Im Spiel ist es das nicht: dort wird mitten im Lauf umgeschaltet,
 * und ein Gangwechsel loeste bisher DREI Durchlaeufe aus — den neuen Gang, und
 * zweimal denselben Gehen-Durchlauf fuer die Akkuauslegung, an zwei Stellen,
 * die beide nur an der Identitaet des Zustandsobjekts haengen.
 *
 * Sichtbar war das als Hakeln beim Umschalten. Genau ein Bild fehlte, jedes
 * Mal.
 *
 * WARUM ER ERLAUBT IST. `runSimulation` ist eine reine Funktion von Modell und
 * Zustand. Es gibt keinen verborgenen Zustand, der ein Ergebnis altern liesse.
 * Zweimal dieselbe Eingabe heisst zweimal dasselbe Ergebnis — dann darf man das
 * zweite auch nachschlagen.
 *
 * WARUM DER SCHLUESSEL AUS DEM INHALT KOMMT UND NICHT AUS DEM VERWEIS. Ein
 * Vergleich der Verweise reicht hier nicht: `setGait` baut `trim` und `task`
 * jedes Mal neu auf. Wer zwischen Gehen und Sprint hin und her schaltet, hat
 * inhaltlich zwei Zustaende und dem Verweis nach vier. Serialisieren kostet
 * Mikrosekunden gegen Millisekunden — das Verhaeltnis macht die Entscheidung.
 *
 * WAS ER NICHT IST: eine Optimierung des Kerns. Der Kern bleibt, wie er ist.
 * Das hier ist eine Schicht der Oberflaeche und steht deshalb in `src/sim`.
 */

/**
 * Wie viele Ergebnisse gehalten werden.
 *
 * Drei Gangarten, dazu Stehen und die Auslegung nach dem Gehen — der Spielfall
 * braucht fuenf. Acht laesst Luft fuer den Wechsel eines Reglers zwischendurch,
 * ohne dass ein Durchlauf mit seinen Kurven ueber den ganzen Zyklus unnoetig
 * lange liegen bleibt.
 */
const PLAETZE = 8

export type RunFn = (model: HumanoidModel, state: SimState) => Simulation

export function createRunCache(): RunFn {
  let fuer: HumanoidModel | null = null
  const gespeichert = new Map<string, Simulation>()

  return (model, state) => {
    /* Ein anderes Modell macht jedes gespeicherte Ergebnis ungueltig. Das
       Modell in den Schluessel zu nehmen waere teurer als der Durchlauf. */
    if (model !== fuer) {
      fuer = model
      gespeichert.clear()
    }

    const schluessel = JSON.stringify(state)
    const treffer = gespeichert.get(schluessel)
    if (treffer) {
      /* Neu einsortieren: eine Map behaelt die Einfuegereihenfolge, damit ist
         der erste Eintrag der am laengsten nicht gebrauchte. */
      gespeichert.delete(schluessel)
      gespeichert.set(schluessel, treffer)
      return treffer
    }

    const ergebnis = runSimulation(model, state)
    gespeichert.set(schluessel, ergebnis)
    if (gespeichert.size > PLAETZE) {
      const aeltester = gespeichert.keys().next().value
      if (aeltester !== undefined) gespeichert.delete(aeltester)
    }
    return ergebnis
  }
}
